import { useEffect, useMemo } from 'react';
import { useQuery, skipToken } from '@tanstack/react-query';
import { apiClient } from '../services/apiClient';
import { useUser } from '../features/auth/useUser';
import {
  PARTNER_BRANDING_CACHE_KEY,
  applyThemeOverrides,
  firePartnerGaReady,
  readPartnerBrandingCache,
  resolveBgImageUrl,
  type PartnerBrandingCache,
} from '../config/brands/applyBrandTheme';
import { brandConfig } from '../config/brands';
import { resolveMenuOrder, type MenuToken } from '../config/menuOrder';

export type PartnerBranding = PartnerBrandingCache;

/** signed URL のクエリ部分を除いたパスだけを返す（比較用） */
function getUrlPath(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url;
  }
}

/**
 * 背景画像を「デコード完了後に」CSS 変数へ適用する。
 *
 * URL を直接 CSS 変数に入れると、ブラウザは画像をダウンロード/デコードしながら
 * 段階的に描画するため、グラデーション→画像の切り替わり (ポップ) が見える。先に
 * Image().decode() で完全にデコードしてからスワップすれば、画像は一度に差し込まれる。
 * decode が失敗 (CORS 非対応等) しても従来どおり URL を適用してフォールバックする。
 */
function applyBgImageDecoded(root: HTMLElement, resolvedUrl: string): void {
  const swap = () => root.style.setProperty('--brand-bg-image', `url(${resolvedUrl})`);
  const img = new Image();
  img.src = resolvedUrl;
  img.decode().then(swap).catch(swap);
}

/**
 * localStorage キャッシュが現在ログイン中の組織のものか検証する。
 *
 * impersonation / ログアウト→別パートナーで再ログイン等で組織が切り替わると、
 * 前テナントの partner_code / stripe_publishable_key がキャッシュに残る。これを
 * そのまま使うと誤った Stripe アカウントの publishable key で決済を初期化し得る
 * ため、組織不一致のキャッシュは「無い」ものとして fail-closed 扱いにする。
 */
function cacheMatchesOrg(
  cache: PartnerBrandingCache | null,
  orgId: string | null,
): boolean {
  return !!cache && !!orgId && cache.organization_id === orgId;
}

/**
 * パートナー管理者がサイト設定で選択した背景画像とトップページ文言を取得する。
 * - 背景画像は副作用として `--brand-bg-image` CSS 変数を更新
 * - 文言は呼び出し側で参照（NULL/空文字なら呼び出し側で非表示にする想定）
 * - 取得結果は localStorage にキャッシュし、次回ロード時の初期レンダリングから
 *   前回値を使って HTML のフラッシュを防ぐ
 * - react-query / localStorage いずれも組織 (organization_id) でスコープし、
 *   テナント跨ぎの誤用を防ぐ
 */
export function usePartnerBranding(enabled: boolean) {
  const orgId = useUser().user?.organization_id ?? null;
  const cached = useMemo(() => readPartnerBrandingCache(), []);

  const { data } = useQuery<PartnerBranding>({
    // organization_id を queryKey に含め、組織が変わったら別キャッシュ＝自動再取得
    queryKey: ['partner-branding', orgId],
    queryFn: async () => {
      const res = await apiClient.get<PartnerBranding>('/partner/branding');
      return res.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!enabled || !data) return;
    const root = document.documentElement;
    if (data.bg_image_url) {
      // 背景 URL は画像 ID に紐づく安定 URL。実体（パス）が同じなら CSS 変数は更新
      // しない。更新するとブラウザが別画像として扱い再取得し、読み込み中に背景が
      // 一瞬消えるフラッシュが発生するため。
      const prevUrl = cached?.bg_image_url ?? null;
      const samePath = getUrlPath(prevUrl) === getUrlPath(data.bg_image_url);
      if (!samePath) {
        // 画像が変わったときだけデコード完了後にスワップし、段階描画のポップを防ぐ。
        const resolved = resolveBgImageUrl(data.bg_image_url);
        if (resolved) applyBgImageDecoded(root, resolved);
      }
    } else {
      root.style.removeProperty('--brand-bg-image');
    }
    // org-admin 編集のテーマ色 override も API 応答に合わせて反映する。
    // (applyBrandTheme は初期ロード時のみ呼ばれるため、保存→反映を待たず
    //  API レスポンスが届いた時点で上書きする)
    applyThemeOverrides(data.theme_overrides, root);
    // 背景の明暗に応じた配色も API 応答時点で反映 (保存→リロード不要)。
    // 優先順位: パートナー設定 > BrandConfig > 'dark'。
    root.setAttribute(
      'data-color-scheme',
      data.color_scheme ?? brandConfig.colorScheme ?? 'dark',
    );
    // 最新値を次回ロード用に保存。どの組織のキャッシュかを organization_id で
    // スタンプし、別テナントで誤用されないようにする。
    localStorage.setItem(
      PARTNER_BRANDING_CACHE_KEY,
      JSON.stringify({ ...data, organization_id: orgId }),
    );
    // パートナー持ち込み GA4 (post-auth 確定値) を GTM へ通知。各IDにつき1回ガードが
    // あるため、起動時キャッシュ/login-branding と同値なら再送されない。
    firePartnerGaReady(data.ga4_measurement_id ?? null);
  }, [data, enabled, cached, orgId]);

  // 視覚ブランディング (背景/文言/メニュー) はフラッシュ回避のため従来どおり
  // data ?? cached を使う (組織不一致でも一瞬の見た目差のみで実害なし)。
  const effective = data ?? cached;
  // 決済に関わる値 (PK/partner_code/settled) は組織一致キャッシュのみ採用。
  const paymentCacheValid = cacheMatchesOrg(cached, orgId);
  const paymentSource = data ?? (paymentCacheValid ? cached : undefined);

  return {
    homeHeroTitle: effective?.home_hero_title ?? '',
    homeHeroSubtitle: effective?.home_hero_subtitle ?? '',
    // 背景画像の有無。暗転オーバーレイは「画像の上で文字を読ませる」ための層なので、
    // 画像が無いブランド (ベース色だけ) では AppLayout 側で無効化する判断に使う。
    bgImageUrl: effective?.bg_image_url ?? null,
    homeOverlayOpacity: effective?.home_overlay_opacity ?? null,
    homeOverlayOpacityOther: effective?.home_overlay_opacity_other ?? null,
    homeCardOpacity: effective?.home_card_opacity ?? null,
    menuOrder: resolveMenuOrder(effective?.menu_order) as MenuToken[],
    scenarioFieldConfig: effective?.scenario_field_config ?? {
      max_scenario_count: 5,
      show_narration: true,
      show_dialogue: true,
      show_telop: true,
    },
    stripePublishableKey: paymentSource?.stripe_publishable_key ?? null,
    partnerCode: paymentSource?.partner_code ?? null,
    // settled = 当組織のサーバ応答取得済み or 当組織の localStorage キャッシュあり。
    // false の間は fail-closed のため Stripe を初期化しない。
    brandingSettled: Boolean(data) || paymentCacheValid,
  };
}

/**
 * Stripe publishable key を runtime に解決する読み取り専用セレクタ。
 * AppLayout が usePartnerBranding(true) を呼んでいる前提で同じ react-query
 * キャッシュ + localStorage から取り出す (追加フェッチしない)。
 *
 * fail-closed 設計: 当組織の branding が未解決の間は settled=false を返し、
 * 呼び出し側は Stripe を初期化しない。publishable key 未配信のパートナーでは
 * partner_code==='default' のときのみ env key へのフォールバックを許可する。
 * テナント跨ぎ防止のため react-query / localStorage とも organization_id で
 * スコープする。
 */
export function useStripePublishableKey(): {
  stripePublishableKey: string | null;
  partnerCode: string | null;
  brandingSettled: boolean;
} {
  const orgId = useUser().user?.organization_id ?? null;
  const cached = useMemo(() => readPartnerBrandingCache(), []);
  // フェッチはせず AppLayout 側が温めたキャッシュだけ購読する。queryFn 無しだと
  // React Query が「No queryFn」を毎レンダー警告するため skipToken を渡す。
  const { data } = useQuery<PartnerBranding>({
    queryKey: ['partner-branding', orgId],
    queryFn: skipToken,
  });
  const paymentCacheValid = cacheMatchesOrg(cached, orgId);
  const source = data ?? (paymentCacheValid ? cached : undefined);
  return {
    stripePublishableKey: source?.stripe_publishable_key ?? null,
    partnerCode: source?.partner_code ?? null,
    brandingSettled: Boolean(data) || paymentCacheValid,
  };
}

/**
 * ScenarioGenerator 等で partner の field 制御情報だけ読みたいときのショートカット。
 * AppLayout が usePartnerBranding(true) を呼んでいる前提で、同じ react-query キャッシュ
 * から値を取り出す (未取得ならデフォルトにフォールバック)。
 */
export function useScenarioFieldConfig() {
  const orgId = useUser().user?.organization_id ?? null;
  const cached = useMemo(() => readPartnerBrandingCache(), []);
  // フェッチはせずキャッシュだけ購読 (skipToken で「No queryFn」警告を回避)。
  const { data } = useQuery<PartnerBranding>({
    queryKey: ['partner-branding', orgId],
    queryFn: skipToken,
  });
  return (
    data?.scenario_field_config
    ?? cached?.scenario_field_config
    ?? {
      max_scenario_count: 5,
      show_narration: true,
      show_dialogue: true,
      show_telop: true,
    }
  );
}
